import React from 'react';
import PropTypes from 'prop-types';
import './fileUpload.css';

import UploadIcon from './UploadIcon.svg';

class ImageUploader extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            pictures: props.pictures ? [...props.pictures] : [],
            files: [],
            notAcceptedFileType: [],
            notAcceptedFileSize: []
        };
        this.inputElement = null;
        this.onDropFile = this.onDropFile.bind(this);
        this.onUploadClick = this.onUploadClick.bind(this);
        this.triggerFileUpload = this.triggerFileUpload.bind(this);
        this.removeImage = this.removeImage.bind(this);
        this.clearPictures = this.clearPictures.bind(this);
    }

    componentDidUpdate(prevProps) {
        if (prevProps.pictures !== this.props.pictures && (!this.props.pictures || this.props.pictures.length === 0)) {
            if (this.state.files.length > 0 || this.state.pictures.length > 0) {
                this.setState({
                    pictures: [],
                    files: [],
                    notAcceptedFileType: [],
                    notAcceptedFileSize: []
                });
                if (this.inputElement) {
                    this.inputElement.value = '';
                }
            }
        }
        if (prevProps.defaultImages !== this.props.defaultImages) {
            this.setState({ pictures: this.props.defaultImages || [] });
        }
    }

    /*
       Check file extension (onDropFile)
    */
    hasExtension(fileName) {
        if (!fileName) return false;
        const pattern = '(' + this.props.imgExtension.join('|').replace(/\./g, '\\.') + ')$';
        return new RegExp(pattern, 'i').test(fileName);
    }

    /*
     Handle file validation
     */
    onDropFile(e) {
        const files = e.target.files;
        if (!files || files.length === 0) return;
        const allFilePromises = [];
        const notAcceptedFileType = [];
        const notAcceptedFileSize = [];

        // Iterate over all uploaded files
        for (let i = 0; i < files.length; i++) {
            let f = files[i];
            // Check for file extension
            if (!this.hasExtension(f.name)) {
                notAcceptedFileType.push(f.name);
                continue;
            }
            // Check for file size
            if (f.size > this.props.maxFileSize) {
                notAcceptedFileSize.push(f.name);
                continue;
            }

            allFilePromises.push(this.readFile(f));
        }

        if (notAcceptedFileType.length > 0 || notAcceptedFileSize.length > 0) {
            this.setState({
                notAcceptedFileType: [...this.state.notAcceptedFileType, ...notAcceptedFileType],
                notAcceptedFileSize: [...this.state.notAcceptedFileSize, ...notAcceptedFileSize]
            });
        }

        Promise.all(allFilePromises).then(newFilesData => {
            const dataURLs = this.props.singleImage ? [] : this.state.pictures.slice();
            const currentFiles = this.props.singleImage ? [] : this.state.files.slice();

            newFilesData.forEach(newFileData => {
                dataURLs.push(newFileData.dataURL);
                currentFiles.push(newFileData.file);
            });

            this.setState({ pictures: dataURLs, files: currentFiles }, () => {
                this.props.onChange(this.state.files, this.state.pictures);
            });
        });
    }

    onUploadClick(e) {
        // Fixes https://github.com/JakeHartnell/react-images-upload/issues/55
        e.target.value = null;
    }

    /*
       Read a file and return a promise that when resolved gives the file itself and the data URL
     */
    readFile(file) {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();

            // Read the file via FileReader API and save result in state.
            reader.onload = function (e) {
                let dataURL = e.target.result;
                if (typeof dataURL === 'string') {
                    dataURL = dataURL.replace(";base64", `;name=${encodeURIComponent(file.name)};base64`);
                }
                resolve({ file, dataURL });
            };

            reader.onerror = function (e) {
                reject(e);
            };

            reader.readAsDataURL(file);
        });
    }

    /*
     Remove the image / document from state
     */
    removeImage(picture, index) {
        const removeIndex = index !== undefined ? index : this.state.pictures.findIndex(e => e === picture);
        if (removeIndex === -1) return;

        const filteredPictures = this.state.pictures.filter((_, i) => i !== removeIndex);
        const filteredFiles = this.state.files.filter((_, i) => i !== removeIndex);

        if (this.inputElement && filteredFiles.length === 0) {
            this.inputElement.value = '';
        }

        this.setState({ pictures: filteredPictures, files: filteredFiles }, () => {
            this.props.onChange(this.state.files, this.state.pictures);
            if (this.props.onDelete) {
                this.props.onDelete(filteredFiles, filteredPictures);
            }
        });
    }

    /*
     Check if any errors && render
     */
    renderErrors() {
        let notAccepted = '';
        if (this.state.notAcceptedFileType.length > 0) {
            notAccepted = this.state.notAcceptedFileType.map((error, index) => {
                return (
                    <div className={'errorMessage ' + this.props.errorClass} key={index} style={this.props.errorStyle}>
                        * {error} {this.props.fileTypeError}
                    </div>
                );
            });
        }
        if (this.state.notAcceptedFileSize.length > 0) {
            notAccepted = this.state.notAcceptedFileSize.map((error, index) => {
                return (
                    <div className={'errorMessage ' + this.props.errorClass} key={index} style={this.props.errorStyle}>
                        * {error} {this.props.fileSizeError}
                    </div>
                );
            });
        }
        return notAccepted;
    }

    /*
     Render the upload icon
     */
    renderIcon() {
        if (this.props.withIcon) {
            return <img src={UploadIcon} className="uploadIcon" alt="Upload Icon" />;
        }
    }

    /*
     Render label
     */
    renderLabel() {
        if (this.props.withLabel) {
            return <p className={this.props.labelClass} style={this.props.labelStyles}>{this.props.label}</p>;
        }
    }

    /*
     Render preview images / documents
     */
    renderPreview() {
        return (
            <div className="uploadPicturesWrapper">
                {this.renderPreviewPictures()}
            </div>
        );
    }

    renderPreviewPictures() {
        return this.state.pictures.map((picture, index) => {
            const file = this.state.files && this.state.files[index];
            const isPdf = (file && (file.type === 'application/pdf' || file.name?.toLowerCase().endsWith('.pdf'))) ||
                          (typeof picture === 'string' && (picture.startsWith('data:application/pdf') || picture.toLowerCase().includes('.pdf')));

            let fileName = file?.name;
            if (!fileName && typeof picture === 'string' && picture.includes(';name=')) {
                try {
                    fileName = decodeURIComponent(picture.split(';name=')[1].split(';')[0]);
                } catch (e) {
                    fileName = 'document.pdf';
                }
            }
            if (!fileName) fileName = isPdf ? 'document.pdf' : `image-${index + 1}`;

            const formattedSize = file?.size
                ? file.size < 1024 * 1024
                    ? `${(file.size / 1024).toFixed(1)} KB`
                    : `${(file.size / (1024 * 1024)).toFixed(2)} MB`
                : null;

            return (
                <div key={index} className="uploadPictureContainer">
                    <div className="deleteImage" onClick={() => this.removeImage(picture, index)} title="Remove file">X</div>
                    {isPdf ? (
                        <div className="pdfPreviewWrapper">
                            <img
                                src="https://node-websites.siddhantait.com/images/pdfimage.png"
                                alt="PDF"
                                className="pdfPreviewIcon"
                                onError={(e) => {
                                    e.target.style.display = 'none';
                                    if (e.target.nextElementSibling) {
                                        e.target.nextElementSibling.style.display = 'flex';
                                    }
                                }}
                            />
                            <div className="pdfFallbackBadge" style={{ display: 'none' }}>
                                PDF
                            </div>
                            <span className="pdfPreviewName" title={fileName}>
                                {fileName}
                            </span>
                            {formattedSize && (
                                <span className="pdfPreviewSize">
                                    {formattedSize}
                                </span>
                            )}
                        </div>
                    ) : (
                        <img src={picture} className="uploadPicture" alt="preview" />
                    )}
                </div>
            );
        });
    }

    /*
     On button click, trigger input file to open
     */
    triggerFileUpload() {
        if (this.inputElement) {
            this.inputElement.click();
        }
    }

    clearPictures() {
        this.setState({ pictures: [], files: [], notAcceptedFileType: [], notAcceptedFileSize: [] });
        if (this.inputElement) {
            this.inputElement.value = '';
        }
    }

    render() {
        return (
            <div className={"fileUploader " + this.props.className} style={this.props.style}>
                <div className="fileContainer" style={this.props.fileContainerStyle}>
                    {this.renderIcon()}
                    {this.renderLabel()}
                    <div className="errorsContainer">
                        {this.renderErrors()}
                    </div>
                    <button
                        type={this.props.buttonType}
                        className={"chooseFileButton " + this.props.buttonClassName}
                        style={this.props.buttonStyles}
                        onClick={this.triggerFileUpload}
                    >
                        {this.props.buttonText}
                    </button>
                    <input
                        type="file"
                        ref={input => this.inputElement = input}
                        name={this.props.name}
                        multiple={!this.props.singleImage}
                        onChange={this.onDropFile}
                        onClick={this.onUploadClick}
                        accept={this.props.accept}
                    />
                    {this.props.withPreview && this.state.pictures.length > 0 ? this.renderPreview() : null}
                </div>
            </div>
        );
    }
}

ImageUploader.defaultProps = {
    className: '',
    fileContainerStyle: {},
    buttonClassName: "",
    buttonStyles: {},
    withPreview: true,
    accept: "image/*,application/pdf,.pdf",
    name: "",
    withIcon: true,
    buttonText: "Choose images",
    buttonType: "button",
    withLabel: true,
    label: "Max file size: 25mb, accepted: jpg|jpeg|png|gif|pdf",
    labelStyles: {},
    labelClass: "",
    imgExtension: ['.jpg', '.jpeg', '.gif', '.png', '.pdf'],
    maxFileSize: 26214400, // 25MB
    fileSizeError: " file size is too big",
    fileTypeError: " is not a supported file extension",
    errorClass: "",
    style: {},
    errorStyle: {},
    singleImage: false,
    onChange: () => { },
    defaultImages: [],
    pictures: []
};

ImageUploader.propTypes = {
    style: PropTypes.object,
    fileContainerStyle: PropTypes.object,
    className: PropTypes.string,
    onChange: PropTypes.func,
    onDelete: PropTypes.func,
    buttonClassName: PropTypes.string,
    buttonStyles: PropTypes.object,
    buttonType: PropTypes.string,
    withPreview: PropTypes.bool,
    accept: PropTypes.string,
    name: PropTypes.string,
    withIcon: PropTypes.bool,
    buttonText: PropTypes.string,
    withLabel: PropTypes.bool,
    label: PropTypes.string,
    labelStyles: PropTypes.object,
    labelClass: PropTypes.string,
    imgExtension: PropTypes.array,
    maxFileSize: PropTypes.number,
    fileSizeError: PropTypes.string,
    fileTypeError: PropTypes.string,
    errorClass: PropTypes.string,
    errorStyle: PropTypes.object,
    singleImage: PropTypes.bool,
    defaultImages: PropTypes.array,
    pictures: PropTypes.array
};

export default ImageUploader;